import React, { useState } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';
import './BbManageModal.css';

/**
 * Blackboard Management Modal Component
 * Handles Blackboard course email domain management
 */
const BbManageModal = ({ bbCourse, onClose }) => {
  const [domains, setDomains] = useState(
    bbCourse.EmailDomains ? JSON.parse(bbCourse.EmailDomains) : []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add new domain
  const addDomain = () => {
    setDomains([...domains, { name: '', email: '' }]);
  };

  // Remove domain
  const removeDomain = (index) => {
    setDomains(domains.filter((_, i) => i !== index));
  };

  // Update domain
  const updateDomain = (index, field, value) => {
    const updatedDomains = [...domains];
    updatedDomains[index][field] = value;
    setDomains(updatedDomains);
  };

  // Save domains
  const saveDomains = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate domains
      const valid = domains.length > 0 && domains.every(d => d.name && d.email);
      if (!valid) {
        setError('Please fill in all domain names and emails');
        setLoading(false);
        return;
      }

      // Save to API (replace with actual API call)
      const token = getTokenForRequest();
      const response = await fetch('/api/bb/updateEmailDomains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token,
          msId: bbCourse.MsID,
          domains: JSON.stringify(domains)
        })
      });

      if (response.ok) {
        onClose();
      } else {
        setError('Failed to save domains');
      }
    } catch (err) {
      setError('Error saving domains');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h5 className="modal-title">
            <i className="fas fa-external-link-alt me-2"></i>
            Manage External Sign Up
          </h5>
          <button type="button" className="btn-close-x" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="domains-section">
            <h6 className="section-title">Email Domains</h6>
            <p className="text-muted">
              Add email domains that are allowed to sign up for this Blackboard course.
            </p>

            {domains.map((domain, index) => (
              <div key={index} className="domain-row">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Domain Name"
                  value={domain.name}
                  onChange={(e) => updateDomain(index, 'name', e.target.value)}
                />
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email Domain"
                  value={domain.email}
                  onChange={(e) => updateDomain(index, 'email', e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => removeDomain(index)}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            ))}

            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={addDomain}
            >
              <i className="fas fa-plus me-1"></i>
              Add Domain
            </button>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={saveDomains}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              'Save Domains'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BbManageModal; 